import React from 'react';
import { Link } from '@inertiajs/react';
import { FaFacebookF, FaTwitter, FaInstagram, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { Footer as FooterType } from '@/types';

interface FooterProps {
  footer: FooterType;
}

export default function Footer({ footer }: FooterProps) {
  const { company, contact, social_media, quick_links, legal } = footer;

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-xl font-semibold mb-4">{company.name}</h3>
            <p className="text-gray-400 mb-4">{company.description}</p>
            <div className="flex space-x-4">
              <a
                href={social_media.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                <FaFacebookF />
              </a>
              <a
                href={social_media.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                <FaTwitter />
              </a>
              <a
                href={social_media.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-primary transition-colors"
              >
                <FaInstagram />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {quick_links.map((link, index) => {
                if(!link || !link.url || !link.name) return null;
                return (
                  <li key={`quick-link-${index}`}>
                    <Link
                      href={link.url || ''}
                      className="text-gray-400 hover:opacity-80 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Contact Info */}
          {contact && contact.address && contact.phone && contact.email && (
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <ul className="space-y-4 text-gray-400">
                <li className="flex items-center space-x-3">
                  <FaMapMarkerAlt className="text-white" />
                  <span>{contact.address}</span>
                </li>
                <li className="flex items-center space-x-3">
                  <FaPhone className="text-white" />
                  <a
                    href={`tel:${contact.phone}`}
                    className="hover:opacity-80 transition-colors"
                  >
                    {contact.phone}
                  </a>
                </li>
                <li className="flex items-center space-x-3">
                  <FaEnvelope className="text-white" />
                  <a
                    href={`mailto:${contact.email}`}
                    className="hover:opacity-80 transition-colors"
                  >
                    {contact.email}
                  </a>
                </li>
              </ul>
            </div>
          )}

          {/* Legal Links */}
          {legal && legal.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                {legal.map((link, index) => {
                  if(!link || !link.url || !link.name) return null;
                  return (
                    <li key={`legal-link-${index}`}>
                      <Link
                        href={link.url}
                        className="text-gray-400 hover:opacity-80 transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-gray-400">
            © {new Date().getFullYear()} {company.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
} 